// Reference-slide picker: browse workspace decks' current-version
// slides (with derived reference/view counts), select up to 5, and
// hand them to the composer as pending slide references.
import { useEffect, useState } from 'react';
import { slideTarget, targetFromPreviewUrl, type ViewerTarget } from './ArtifactViewer';
import SlideThumb from './SlideThumb';
import { ArrowBendUpLeft, Eye } from '@phosphor-icons/react';

export interface SlideRef {
  deck_id: string;
  version: string;
  slide_num: number;
  deck_title: string;
}

interface CatalogSlide {
  slide_num: number;
  references: number;
  views: number;
  preview_url: string | null;
}
interface CatalogDeck {
  deck_id: string;
  title: string;
  version: string;
  slides: CatalogSlide[];
}

const MAX_REFS = 5;

export default function SlidePicker({
  workspaceId,
  currentDeckId,
  selected,
  onChange,
  onClose,
  onPreview,
}: {
  workspaceId: string;
  currentDeckId: string;
  selected: SlideRef[];
  onChange: (refs: SlideRef[]) => void;
  onClose: () => void;
  /** T2.1 — open the full slide in the artifact viewer (renders above the picker). */
  onPreview?: (t: ViewerTarget) => void;
}) {
  const [decks, setDecks] = useState<CatalogDeck[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searchHits, setSearchHits] = useState<Array<{ deck_id: string; deck_title: string; version: string; slide_num: number; snippet: string }> | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setSearchHits(null); return; }
    const t = window.setTimeout(() => {
      fetch(`/api/workspaces/${workspaceId}/slide-search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : { results: [] }))
        .then((d: { results: typeof searchHits }) => setSearchHits(d.results ?? []))
        .catch(() => setSearchHits([]));
    }, 300);
    return () => window.clearTimeout(t);
  }, [query, workspaceId]);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/workspaces/${workspaceId}/slide-catalog`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d: { decks: CatalogDeck[] }) => {
        if (!cancelled) setDecks(d.decks.filter(x => x.deck_id !== currentDeckId));
      })
      .catch(e => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [workspaceId, currentDeckId]);

  const isSelected = (d: CatalogDeck, n: number) =>
    selected.some(r => r.deck_id === d.deck_id && r.version === d.version && r.slide_num === n);

  const toggle = (d: CatalogDeck, s: CatalogSlide) => {
    if (isSelected(d, s.slide_num)) {
      onChange(selected.filter(r => !(r.deck_id === d.deck_id && r.version === d.version && r.slide_num === s.slide_num)));
    } else if (selected.length < MAX_REFS) {
      onChange([...selected, { deck_id: d.deck_id, version: d.version, slide_num: s.slide_num, deck_title: d.title }]);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--ds-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-primary, #fff)', borderRadius: 12, width: 'min(860px, 92vw)', maxHeight: '80vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <strong>Reference slides from this workspace ({selected.length}/{MAX_REFS})</strong>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search slide text…"
            style={{ marginLeft: 16, flex: 1, maxWidth: 280, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--ds-shadow-color)', fontSize: 12 }}
          />
          <button onClick={onClose} aria-label="Close picker" style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
        </div>
        {error && <div style={{ color: 'var(--ds-danger)' }}>Could not load catalog: {error}</div>}
        {!decks && !error && <div>Loading…</div>}
        {decks && decks.length === 0 && <div>No other decks with built slides in this workspace yet.</div>}
        {searchHits && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, margin: '4px 0' }}>Search results ({searchHits.length})</div>
            {searchHits.length === 0 && <div style={{ opacity: 0.55, fontSize: 12 }}>No slides match "{query}".</div>}
            {searchHits.filter(h => h.deck_id !== currentDeckId).map(h => {
              const d = decks?.find(x => x.deck_id === h.deck_id);
              const cd: CatalogDeck = d ?? { deck_id: h.deck_id, title: h.deck_title, version: h.version, slides: [] };
              const sel = isSelected(cd, h.slide_num);
              return (
                <div
                  key={`${h.deck_id}-${h.slide_num}`}
                  onClick={() => toggle(cd, { slide_num: h.slide_num, references: 0, views: 0, preview_url: null })}
                  style={{ padding: '6px 10px', borderRadius: 8, cursor: 'pointer', border: sel ? '2px solid var(--ds-accent)' : '1px solid var(--ds-shadow-color)', marginBottom: 6, fontSize: 12 }}
                >
                  <div style={{ fontWeight: 600 }}>{h.deck_title} · {h.version} · slide {h.slide_num}</div>
                  <div style={{ opacity: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.snippet}</div>
                </div>
              );
            })}
          </div>
        )}
        {decks?.map(d => (
          <div key={d.deck_id} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, margin: '8px 0' }}>{d.title} <span style={{ opacity: 0.6, fontWeight: 400 }}>({d.version})</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {d.slides.map(s => (
                <div
                  key={s.slide_num}
                  onClick={() => toggle(d, s)}
                  style={{
                    border: isSelected(d, s.slide_num) ? '2px solid var(--ds-accent)' : '1px solid var(--ds-shadow-color)',
                    borderRadius: 8, cursor: 'pointer', overflow: 'hidden', background: 'var(--ds-surface)',
                  }}
                >
                  {s.preview_url ? (
                    <SlideThumb src={s.preview_url} title={`${d.title} slide ${s.slide_num}`} />
                  ) : (
                    <div style={{ aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>Slide {s.slide_num}</div>
                  )}
                  <div style={{ padding: '6px 8px', fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Slide {s.slide_num}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, opacity: 0.65 }} title="references · views">
                      <ArrowBendUpLeft size={11} weight="light" /> {s.references}
                      <span style={{ opacity: 0.5, margin: '0 2px' }}>·</span>
                      <Eye size={11} weight="light" /> {s.views}
                    </span>
                  </div>
                  {onPreview && (
                    <div
                      onClick={e => {
                        e.stopPropagation();
                        const t = (s.preview_url && targetFromPreviewUrl(d.deck_id, s.preview_url))
                          || slideTarget(d.deck_id, d.version, s.slide_num);
                        onPreview({ ...t, source: { deckTitle: d.title, slideNum: s.slide_num, deckId: d.deck_id, version: d.version } });
                      }}
                      title="Open the full slide"
                      style={{ padding: '0 8px 6px', fontSize: 11, color: 'var(--ds-accent)', cursor: 'pointer' }}
                    >View full slide ↗</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid var(--ds-shadow-color)', cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}
